import { gql, type TypedDocumentNode } from '@apollo/client';
import classNames from 'classnames';
import type { ComponentProps, CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ShieldButton } from '@sorare/core/src/atoms/buttons/ShieldButton';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons/index.tsx';
import { iconLock } from '@sorare/core/src/atoms/icons/system/iconLock.ts';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandXS } from '@sorare/core/src/atoms/typography/index.tsx';
import { range } from '@sorare/core/src/lib/arrays.ts';

import { PickerOption } from '#src/components/pickers/BoardPicker/PickerCard.tsx';
import { PickerHeader } from '#src/components/pickers/PickerHeader/index.tsx';
import { PickerOptionsGrid } from '#src/components/pickers/PickerOptionsGrid.tsx';

import type { LeaderboardGroupPicker_so5LeaderboardGroup } from './__generated__/index.graphql.ts';

const GroupIcon = styled.img`
  width: var(--triple-unit);
  height: var(--triple-unit);
  object-fit: contain;

  &.md {
    width: var(--quadruple-unit);
    height: var(--quadruple-unit);
    margin-top: var(--intermediate-unit);
  }
`;

const StyledPickerOption = styled(PickerOption)`
  padding: var(--triple-unit);
  aspect-ratio: 1;
`;

const LockOverlay = styled(FontAwesomeIcon)`
  position: absolute;
  top: var(--unit);
  left: var(--unit);
  color: var(--c-nd-400);
`;

const GridIcon = styled.img`
  width: calc(5 * var(--unit));
  height: calc(5 * var(--unit));
  object-fit: contain;
`;

const Dot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: 1px solid var(--c-white);
  &.active {
    background-color: var(--c-onBase-success);
    border: 1px solid var(--c-onBase-success);
  }
`;

type Props = {
  so5LeaderboardGroups: readonly LeaderboardGroupPicker_so5LeaderboardGroup[];
  selectedGroup?: string;
  onSelect: (slug: string) => void;
} & Pick<ComponentProps<typeof ShieldButton>, 'size'>;

export const LeaderboardGroupPicker = ({
  so5LeaderboardGroups,
  selectedGroup,
  onSelect,
  size = 'sm',
}: Props) => {
  const selectedGroupData = so5LeaderboardGroups.find(
    g => g.slug === selectedGroup
  );

  return (
    <Dropdown
      align="center"
      gap={8}
      label={props => (
        <ShieldButton
          type="button"
          size={size}
          icon={
            selectedGroupData?.iconUrl ? (
              <GroupIcon
                className={size === 'md' ? 'md' : undefined}
                src={selectedGroupData.iconUrl}
                alt=""
                role="presentation"
              />
            ) : undefined
          }
          orientation="down"
          {...props}
        />
      )}
    >
      {({ closeDropdown }) => (
        <>
          <PickerHeader onClose={closeDropdown}>
            <FormattedMessage defaultMessage="Choose Competition" />
          </PickerHeader>
          <PickerOptionsGrid>
            {so5LeaderboardGroups.map(group => {
              const teamsCap = group.so5Leaderboards.at(0)?.teamsCap;

              return (
                <StyledPickerOption
                  key={group.slug}
                  disabled={!group.hasLeaderboards}
                  className={classNames({
                    selected: selectedGroup === group.slug,
                  })}
                  onClick={() => {
                    const slug = group.so5Leaderboards.at(0)?.slug;
                    if (slug) {
                      onSelect(slug);
                      closeDropdown();
                    }
                  }}
                  style={{ '--board-color': group.color } as CSSProperties}
                >
                  {!group.hasLeaderboards && <LockOverlay icon={iconLock} />}
                  <Vertical center gap={2}>
                    {!!group.iconUrl && (
                      <GridIcon
                        src={group.iconUrl}
                        alt={group.displayName ?? ''}
                      />
                    )}
                    {!!group.hasLeaderboards &&
                      (teamsCap ? (
                        <Horizontal>
                          {range(teamsCap).map(i => (
                            <Dot
                              key={i}
                              className={classNames({
                                active: i < group.mySo5LineupsCount,
                              })}
                            />
                          ))}
                        </Horizontal>
                      ) : (
                        <BrandXS>{group.mySo5LineupsCount}</BrandXS>
                      ))}
                  </Vertical>
                </StyledPickerOption>
              );
            })}
          </PickerOptionsGrid>
        </>
      )}
    </Dropdown>
  );
};

LeaderboardGroupPicker.fragments = {
  so5LeaderboardGroup: gql`
    fragment LeaderboardGroupPicker_so5LeaderboardGroup on So5LeaderboardGroup {
      slug
      iconUrl
      displayName
      mySo5LineupsCount
      hasLeaderboards
      color(variant: PRIMARY)
      so5Leaderboards {
        slug
        teamsCap
      }
    }
  ` as TypedDocumentNode<LeaderboardGroupPicker_so5LeaderboardGroup>,
};
